import TimelapseOutlinedIcon from "@mui/icons-material/TimelapseOutlined";
import { Box, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatElapsed, type TurnPhase } from "@/features/chat/lib/turnPhase";
import {
  formatStepDuration,
  type SubStepTiming,
  type TimeSpan,
  type TurnTiming,
} from "@/features/chat/lib/turnTiming";

type TurnTimingBreakdownProps = {
  timing: TurnTiming;
};

type BreakdownRow = {
  key: string;
  label: string;
  ms: number;
  /** Where the row sits on the turn's timeline; empty when the turn has no timeline. */
  spans: TimeSpan[];
  /** A model or tool call inside the phase above it: part of that phase's time, not added. */
  isSubStep: boolean;
  isOther: boolean;
  /** For a call: the other calls that ran at the same time; empty when it ran alone. */
  parallelWith: string[];
};

function subStepRows(subSteps: SubStepTiming[]): BreakdownRow[] {
  return subSteps.map((subStep) => ({
    key: `step-${subStep.stepIndex}`,
    label: subStep.label,
    ms: subStep.ms,
    spans: subStep.span ? [subStep.span] : [],
    isSubStep: true,
    isOther: false,
    parallelWith: subStep.parallelWith,
  }));
}

/**
 * The heading's key. The last phase writes why and action after the research; on a turn with no
 * research (a greeting, a widget draft) it writes the whole answer instead.
 */
function phaseHeadingKey(phase: TurnPhase, timing: TurnTiming): string {
  const researched = timing.byPhase.some((entry) => entry.phase === "explaining");
  const heading = phase === "finishing" && researched ? "finishingAfterResearch" : phase;
  return `response.howGenerated.phases.${heading}`;
}

function barColor(row: BreakdownRow): string {
  if (row.isOther) {
    return "var(--theme-overlay-light-10)";
  }
  if (!row.isSubStep) {
    return "var(--theme-primary)";
  }
  return row.parallelWith.length > 0 ? "var(--theme-info)" : "var(--theme-primary-light)";
}

/** The row's stretches placed on a track spanning the whole turn. */
function TimelineTrack({ row, totalMs }: { row: BreakdownRow; totalMs: number }) {
  return (
    <Box
      sx={{
        position: "relative",
        height: row.isSubStep ? 4 : 6,
        borderRadius: 999,
        bgcolor: "var(--theme-overlay-light-05)",
      }}
    >
      {row.spans.map((span) => (
        <Box
          key={`${span.startMs}-${span.endMs}`}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${(span.startMs / totalMs) * 100}%`,
            width: `${Math.max(0.5, ((span.endMs - span.startMs) / totalMs) * 100)}%`,
            borderRadius: 999,
            bgcolor: barColor(row),
          }}
        />
      ))}
    </Box>
  );
}

/** Turns recorded before steps carried their position: bars sized against the widest row. */
function ProportionalBar({ row, widestMs }: { row: BreakdownRow; widestMs: number }) {
  return (
    <Box
      sx={{
        height: row.isSubStep ? 4 : 6,
        borderRadius: 999,
        width: `${Math.max(2, Math.round((row.ms / widestMs) * 100))}%`,
        bgcolor: barColor(row),
      }}
    />
  );
}

/** On a call's row: it ran alongside other calls, named in the tooltip. */
function ParallelMark({ row }: { row: BreakdownRow }) {
  const { t } = useTranslation("chat");
  return (
    <Tooltip
      title={t("response.howGenerated.ranAlongside", { steps: row.parallelWith.join(", ") })}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          ml: 1,
          px: 0.75,
          borderRadius: 999,
          border: "1px solid var(--theme-info)",
          color: "var(--theme-info)",
          fontSize: "0.65rem",
          lineHeight: 1.6,
        }}
      >
        {t("response.howGenerated.parallel")}
      </Typography>
    </Tooltip>
  );
}

function TimelineAxis({ totalMs }: { totalMs: number }) {
  const ticks = [0, totalMs / 2, totalMs];
  return (
    <Box
      sx={{ display: "flex", justifyContent: "space-between" }}
      aria-hidden
    >
      {ticks.map((tick) => (
        <Typography
          key={tick}
          variant="caption"
          sx={{ color: "var(--theme-text-secondary)", fontVariantNumeric: "tabular-nums" }}
        >
          {formatElapsed(tick / 1000)}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * "Answered in 1m 38s" at the top of "How it was generated", with a small clock icon that opens
 * where the time went: one row per phase, placed on a timeline of the whole turn when the steps
 * carry their position, with overlapping stretches counted once in a phase's time. A holder of
 * `chat-call-timing-view` also sees each model and tool call under its phase (`summarizeTurnTiming`'s
 * `includeCalls`), with calls that ran side by side marked "Parallel".
 */
export function TurnTimingBreakdown({ timing }: TurnTimingBreakdownProps) {
  const { t } = useTranslation("chat");
  const [open, setOpen] = useState(false);
  const totalMs = timing.totalMs;
  if (totalMs === null) {
    return null;
  }
  const rows: BreakdownRow[] = [
    ...timing.byPhase.flatMap((entry): BreakdownRow[] => [
      {
        key: entry.phase,
        label: t(phaseHeadingKey(entry.phase, timing)),
        ms: entry.ms,
        spans: entry.spans,
        isSubStep: false,
        isOther: false,
        parallelWith: [],
      },
      ...subStepRows(entry.subSteps),
    ]),
    ...(timing.otherMs > 0 || timing.otherSubSteps.length > 0
      ? [
          {
            key: "other",
            label: t("response.howGenerated.otherTime"),
            ms: timing.otherMs,
            spans: timing.otherSpans,
            isSubStep: false,
            isOther: true,
            parallelWith: [],
          },
          ...subStepRows(timing.otherSubSteps),
        ]
      : []),
  ];
  const widestMs = Math.max(...rows.map((row) => row.ms), 1);
  const onTimeline = timing.hasTimeline && totalMs > 0;

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: "1px solid var(--theme-border)",
        bgcolor: "var(--theme-background)",
        px: 1.5,
        py: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "var(--theme-text-primary)" }}>
          {t("response.howGenerated.answeredIn", {
            duration: formatElapsed(totalMs / 1000),
          })}
        </Typography>
        <Tooltip title={t("response.howGenerated.timeBreakdown")}>
          <IconButton
            size="small"
            aria-label={t("response.howGenerated.timeBreakdown")}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            sx={{ color: open ? "var(--theme-primary)" : "var(--theme-text-secondary)" }}
          >
            <TimelapseOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          component="dl"
          sx={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: "6px 12px", m: 0, mt: 1 }}
        >
          {onTimeline ? (
            <>
              <Box />
              <TimelineAxis totalMs={totalMs} />
              <Box />
            </>
          ) : null}
          {rows.map((row) => (
            <Box key={row.key} sx={{ display: "contents" }}>
              <Typography
                component="dt"
                variant="caption"
                sx={{
                  color: "var(--theme-text-secondary)",
                  whiteSpace: "nowrap",
                  pl: row.isSubStep ? 2 : 0,
                }}
              >
                {row.label}
                {row.parallelWith.length > 0 ? <ParallelMark row={row} /> : null}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }} aria-hidden>
                {onTimeline ? (
                  <TimelineTrack row={row} totalMs={totalMs} />
                ) : (
                  <ProportionalBar row={row} widestMs={widestMs} />
                )}
              </Box>
              <Typography
                component="dd"
                variant="caption"
                sx={{
                  m: 0,
                  color: row.isSubStep
                    ? "var(--theme-text-secondary)"
                    : "var(--theme-text-primary)",
                  fontVariantNumeric: "tabular-nums",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {row.isSubStep ? formatStepDuration(row.ms) : formatElapsed(row.ms / 1000)}
                {` · ${Math.round((row.ms / totalMs) * 100)}%`}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
